/**
 * Notification Service
 * ────────────────────
 * Browser Notification API-based reminders.
 * Checks every minute if it's time to send a reminder.
 * Runs as a React component mounted in the app root.
 *
 * Phase 1: Browser notifications (requires app open in tab)
 * Phase 2 (future): Firebase Cloud Messaging for background push
 */
import { useEffect, useRef } from 'react';
import { useProfilePrefs } from '@/lib/profile-prefs';
import { useScheduleStore } from '@/lib/schedule-store';
import { localToday } from '@/lib/date-utils';

// Track which notifications have been sent today to avoid duplicates
const sentToday = new Set<string>();

function resetSentIfNewDay() {
  const today = localToday();
  const storedDay = localStorage.getItem('kader-notif-day');
  if (storedDay !== today) {
    sentToday.clear();
    localStorage.setItem('kader-notif-day', today);
  }
}

async function requestPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

function sendNotification(title: string, body: string, tag: string) {
  if (sentToday.has(tag)) return;
  sentToday.add(tag);

  try {
    new Notification(title, {
      body,
      icon: '/favicon.svg',
      tag, // Prevents duplicate notifications with same tag
      silent: false,
    });
  } catch {
    // Notification API not available (e.g., some mobile browsers)
  }
}

export default function NotificationService() {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const permissionGranted = useRef(false);

  useEffect(() => {
    // Request permission on mount
    requestPermission().then(granted => {
      permissionGranted.current = granted;
    });

    // Set up FCM foreground listener for push notifications when app is open
    import('@/lib/push-notifications').then(({ setupForegroundListener, isPushSupported }) => {
      if (isPushSupported()) setupForegroundListener();
    }).catch(() => { /* FCM not available */ });

    // Check every 60 seconds
    intervalRef.current = setInterval(() => {
      // Re-check permission status in case user changed it in browser settings
      if (!permissionGranted.current && 'Notification' in window && Notification.permission === 'granted') {
        permissionGranted.current = true;
      }
      if (!permissionGranted.current) return;
      resetSentIfNewDay();

      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
      const prefs = useProfilePrefs.getState();
      const schedule = useScheduleStore.getState();
      const today = localToday();
      const dayState = schedule.getDayState(today);

      // ─── Log Reminder ─────────────────────────────────────────────
      if (prefs.notifications.logReminder && currentTime === prefs.logReminderTime) {
        // Check if today's session is not yet logged
        if (!dayState.sessionCompleted && !dayState.sessionSkipped) {
          sendNotification(
            'Kader — Log your training',
            "Don't forget to log today's session. Your coach needs the data.",
            `log-${today}`
          );
        }
      }

      // ─── Morning Brief Reminder ───────────────────────────────────
      if (prefs.notifications.morningBrief && currentTime === prefs.briefTime) {
        sendNotification(
          'Kader — Morning Brief ready',
          'Your daily coaching brief is waiting. Check your plan for today.',
          `brief-${today}`
        );
      }

      // ─── Post-Session Reminder (2 hours after log reminder if still not logged) ──
      if (prefs.notifications.postSession) {
        const reminderHour = parseInt(prefs.logReminderTime.split(':')[0]!) + 2;
        const lateTime = `${String(Math.min(reminderHour, 23)).padStart(2, '0')}:${prefs.logReminderTime.split(':')[1]}`;
        if (currentTime === lateTime && !dayState.sessionCompleted && !dayState.sessionSkipped) {
          sendNotification(
            'Kader — Session still unlogged',
            'Your coach is waiting for your training data. Log it now or mark as skipped.',
            `late-${today}`
          );
        }
      }
    }, 60_000); // Every minute

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // This component renders nothing — it's a background service
  return null;
}
